import { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Card } from '@components/atoms/Card';
import { Checkbox } from '@components/atoms/Checkbox';
import Alert from '@components/atoms/Alert';
import { NmiKeyTutorialModal } from '@components/organisms/organization/NmiKeyTutorialModal';
import { useConnectNmiAccount, useMyPaymentAccount } from '@hooks/api/merchantPaymentAccount';
import { MerchantAccountStatus } from '@models/merchantPaymentAccount.model';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { copyToClipboard } from '@utils/copyToClipboard';

// TODO: swap for the live Swipesum onboarding form URL
const SWIPESUM_FORM_URL =
  'https://swipesum.iriscrm.com/form/preview/zenhammer-intake-form-pre-app';

// Human help: schedule time with Swipesum (Peter), or call our support line.
const SWIPESUM_CALENDLY_URL =
  'https://calendly.com/pfernandes-swipesum/zenhammer-swipesum';
const SUPPORT_PHONE_DISPLAY = '(844) 356-9905';
const SUPPORT_PHONE_TEL = '+18443569905';

type WizardStep = 'choose' | 'connect' | 'preflight' | 'help';

interface ConnectPaymentAccountModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: () => void;
}

export const ConnectPaymentAccountModal: FC<ConnectPaymentAccountModalProps> = ({
  open,
  onClose,
  onConnected,
}) => {
  const paymentAccount = useMyPaymentAccount();
  const connectAccount = useConnectNmiAccount();

  const [step, setStep] = useState<WizardStep>('choose');
  // Remembered so "Back" from the help view returns to where the user was.
  const [stepBeforeHelp, setStepBeforeHelp] = useState<WizardStep>('choose');
  const [showTutorial, setShowTutorial] = useState(false);
  const [checklist, setChecklist] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  const validationSchema = Yup.object({
    securityKey: Yup.string()
      .trim()
      .required('Security key is required')
      .min(8, 'Security key looks too short'),
  });

  const formik = useFormik({
    initialValues: { securityKey: '' },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await connectAccount.mutateAsync({ securityKey: values.securityKey.trim() });
        // Success toast handled by the hook; the invalidated cache refetches the account.
        onConnected?.();
        onClose();
      } catch {
        // Error toast surfaced by the hook.
      }
    },
  });

  const { values, handleChange, submitForm, errors, touched, resetForm } = formik;

  // Reset wizard state every time the modal opens. Always start at 'choose'.
  useEffect(() => {
    if (open) {
      setStep('choose');
      setChecklist([false, false, false, false]);
    } else {
      resetForm();
    }
  }, [open, resetForm]);

  const connected = paymentAccount.data;
  const isConnected =
    !!connected && connected.status === MerchantAccountStatus.CONNECTED;

  const toggleChecklist = (index: number) => {
    setChecklist((prev) =>
      prev.map((value, i) => (i === index ? !value : value)),
    );
  };

  const allChecked = checklist.every(Boolean);

  const checklistItems = [
    'Government-issued ID',
    'Proof of business (business license / articles of organization)',
    'Bank letter or voided check',
    'Your Tax ID (TIN) and bank routing number on hand',
  ];

  const handleContinueToForm = () => {
    window.open(SWIPESUM_FORM_URL, '_blank', 'noopener');
  };

  const openHelp = () => {
    setStepBeforeHelp(step);
    setStep('help');
  };

  const modalTitle =
    step === 'choose'
      ? 'Set up invoicing'
      : step === 'connect'
        ? 'Connect your payment account'
        : step === 'help'
          ? 'Need a hand?'
          : 'Set up my payments';

  const modalDescription =
    step === 'choose'
      ? 'Pick the option that matches where you are.'
      : step === 'connect'
        ? 'To send invoices and get paid, connect your organization to NMI by pasting your security key below.'
        : step === 'help'
          ? 'Talk to a person or see the step-by-step guide.'
          : undefined;

  return (
    <>
      <Modal
      isOpen={open}
      onClose={onClose}
      title={modalTitle}
      description={modalDescription}
      size="medium"
      closeOnOverlayClick={!connectAccount.isPending}
      closeOnEscape={!connectAccount.isPending}>
      {/* Step: choose ------------------------------------------------------ */}
      {step === 'choose' && (
        <div className="flex flex-col gap-3">
          {isConnected && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700 ring-1 ring-green-200">
              <CheckCircleRoundedIcon fontSize="small" />
              Payments connected · ••••{connected?.last4}
            </div>
          )}

          {[
            {
              key: 'connect' as const,
              icon: <VpnKeyRoundedIcon fontSize="small" />,
              title: 'I already have my account',
              body: 'I finished the setup and was verified — paste my NMI key to connect.',
              go: () => setStep('connect'),
            },
            {
              key: 'preflight' as const,
              icon: <RocketLaunchRoundedIcon fontSize="small" />,
              title: 'Set up my payments',
              body: "I haven't done this yet — walk me through the onboarding form.",
              go: () => setStep('preflight'),
            },
          ].map((choice) => (
            <Card
              key={choice.key}
              role="button"
              tabIndex={0}
              onClick={choice.go}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  choice.go();
                }
              }}
              className="group flex cursor-pointer items-center gap-4 rounded-xl border border-gray-200 p-4 shadow-none ring-0 transition-[border-color,box-shadow] hover:border-indigo-400 hover:shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100">
                {choice.icon}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="m-0 text-sm font-semibold text-gray-900">{choice.title}</h3>
                <p className="mt-0.5 mb-0 text-sm leading-snug text-gray-500">{choice.body}</p>
              </div>
              <ChevronRightRoundedIcon
                className="shrink-0 text-gray-300 transition-colors group-hover:text-indigo-500"
                fontSize="small"
              />
            </Card>
          ))}
        </div>
      )}

      {/* Step: connect (Path A) — existing paste-key form ------------------ */}
      {step === 'connect' && (
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            {isConnected && (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700 ring-1 ring-green-200">
                <CheckCircleRoundedIcon fontSize="small" />
                Connected · ••••{connected?.last4}
              </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <p className="m-0 mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                How to find your NMI key
              </p>
              <ol className="m-0 flex list-decimal flex-col gap-2 pl-5 text-sm text-gray-600 dark:text-gray-300">
                <li>
                  Open your{' '}
                  <a
                    href="https://swipesum.transactiongateway.com/merchants"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-indigo-600 underline hover:text-indigo-700 dark:text-indigo-400">
                    NMI merchant portal
                  </a>
                </li>
                <li>
                  Go to <span className="font-medium">My Settings → Gateway Options → Security Keys</span>
                </li>
                <li>
                  Click <span className="font-medium">“Add a New Private Key”</span> and
                  choose key type <span className="font-medium">“API”</span>
                </li>
                <li>
                  Name it <span className="font-medium">“Zenhammer”</span> and click
                  Create
                </li>
                <li>Copy the key and paste it below</li>
              </ol>
            </div>

            <button
              type="button"
              onClick={() => setShowTutorial(true)}
              className="self-start text-sm font-medium text-indigo-600 hover:text-indigo-700">
              Need the full guide with screenshots? →
            </button>

            <FormGroup
              error={(touched.securityKey && errors.securityKey) || undefined}
              htmlFor="securityKey"
              label="NMI security key">
              <Input
                name="securityKey"
                type="password"
                value={values.securityKey}
                onChange={handleChange}
                placeholder="Paste your NMI security key"
                error={!!(touched.securityKey && errors.securityKey)}
              />
            </FormGroup>
          </form>

          <div className="flex justify-between items-center gap-3">
            <Button
              variant="tertiary"
              onClick={() => setStep('choose')}
              disabled={connectAccount.isPending}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={submitForm}
              loading={connectAccount.isPending}
              disabled={connectAccount.isPending}>
              {connectAccount.isPending ? 'Connecting…' : 'Connect account'}
            </Button>
          </div>
        </div>
      )}

      {/* Step: preflight (Path B) — checklist + form link ------------------ */}
      {step === 'preflight' && (
        <div className="flex flex-col gap-4">
          <p className="m-0 text-sm font-medium text-gray-900 dark:text-white">
            Before you start, have these ready
          </p>

          <div className="flex flex-col gap-3">
            {checklistItems.map((item, index) => (
              <Checkbox
                key={index}
                checked={checklist[index]}
                onChange={() => toggleChecklist(index)}
                label={item}
              />
            ))}
          </div>

          <Button
            variant="primary"
            fullWidth
            disabled={!allChecked}
            onClick={handleContinueToForm}>
            Continue to the form
          </Button>

          <Alert variant="info" showIcon border>
            After you submit and get approved, come back here and connect your
            API key.
          </Alert>

          <div className="flex justify-between items-center gap-3">
            <Button variant="tertiary" onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button variant="secondary" onClick={() => setStep('connect')}>
              I already have my key
            </Button>
          </div>
        </div>
      )}

      {/* Step: help — human contact + instructions for a blocked user --------- */}
      {step === 'help' && (
        <div className="flex flex-col gap-3">
          {[
            {
              key: 'schedule',
              icon: <EventAvailableRoundedIcon fontSize="small" />,
              title: 'Schedule a call with our payments partner',
              body: 'Book time with Peter at Swipesum to get set up or unblocked.',
              go: () =>
                window.open(SWIPESUM_CALENDLY_URL, '_blank', 'noopener'),
            },
            {
              key: 'call',
              icon: <PhoneRoundedIcon fontSize="small" />,
              title: `Call support · ${SUPPORT_PHONE_DISPLAY}`,
              body: "Quick question? Our team can usually walk you through it faster. It's also listed at the top of the intake form.",
              go: () => {
                window.location.href = `tel:${SUPPORT_PHONE_TEL}`;
              },
            },
            {
              key: 'instructions',
              icon: <MenuBookRoundedIcon fontSize="small" />,
              title: 'See step-by-step instructions',
              body: 'Read the guide with screenshots for finding your NMI key.',
              go: () => setShowTutorial(true),
            },
          ].map((choice) => (
            <Card
              key={choice.key}
              role="button"
              tabIndex={0}
              onClick={choice.go}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  choice.go();
                }
              }}
              className="group flex cursor-pointer items-center gap-4 rounded-xl border border-gray-200 p-4 shadow-none ring-0 transition-[border-color,box-shadow] hover:border-indigo-400 hover:shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100">
                {choice.icon}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="m-0 text-sm font-semibold text-gray-900">{choice.title}</h3>
                <p className="mt-0.5 mb-0 text-sm leading-snug text-gray-500">{choice.body}</p>
              </div>
              {choice.key === 'call' && (
                <button
                  type="button"
                  aria-label="Copy phone number"
                  title="Copy phone number"
                  onClick={(e) => {
                    // Don't trigger the card's tel: action — just copy the number.
                    e.stopPropagation();
                    void copyToClipboard(SUPPORT_PHONE_DISPLAY, {
                      successMessage: 'Phone number copied',
                    });
                  }}
                  className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600">
                  <ContentCopyRoundedIcon fontSize="small" />
                </button>
              )}
              <ChevronRightRoundedIcon
                className="shrink-0 text-gray-300 transition-colors group-hover:text-indigo-500"
                fontSize="small"
              />
            </Card>
          ))}

          <div className="flex justify-start">
            <Button variant="tertiary" onClick={() => setStep(stepBeforeHelp)}>
              Back
            </Button>
          </div>
        </div>
      )}

      {/* Persistent help entry point — available on every step but the help view. */}
      {step !== 'help' && (
        <div className="mt-5 flex items-center justify-center gap-1.5 border-t border-gray-100 pt-3 text-sm text-gray-500">
          <SupportAgentRoundedIcon fontSize="small" className="text-gray-400" />
          <span>Stuck?</span>
          <button
            type="button"
            onClick={openHelp}
            className="font-medium text-indigo-600 hover:text-indigo-700">
            Talk to a person or get help
          </button>
        </div>
      )}
      </Modal>

      <NmiKeyTutorialModal
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        onDone={() => setShowTutorial(false)}
      />
    </>
  );
};

export default ConnectPaymentAccountModal;
